<template>
  <div class="page-editor">
   
    <div class="mb-3">
      <label class="form-label">Page title</label>
      <input v-model="pageData.label" type="text" class="form-control" placeholder="e.g. Questions about chapter 1" />
    </div>

    <div class="mb-3">
      <label class="form-label">Description (optional)</label>
      <textarea v-model="pageData.description" class="form-control" rows="2"
        placeholder="Description or instructions for this page"></textarea>
    </div>

    <!-- Items in this page -->
    <div class="mb-3">
      <label class="form-label">Items in this page</label>

      <div v-if="childNodes.length === 0" class="alert alert-info">
        <i class="fas fa-info-circle me-2"></i>
        No items yet. Pleaser add a question.
      </div>

      <div v-else class="items-list">
        <div v-for="(child, index) in childNodes" :key="child.id" class="item-card mb-2" :class="{ 'pulse-animation': newlyAddedNodeId === child.id }">
          <div class="d-flex align-items-center">
            <span class="item-number">{{ index + 1 }}</span>
            <div class="flex-grow-1 ms-2">
              <strong>{{ child.data?.label || 'Untitled' }}</strong>
              <br>
              <small class="text-muted">
                Type: {{ getTypeLabel(child.data?.subtype || child.type) }}
              </small>
            </div>
            <div class="btn-group btn-group-sm">
              <button class="btn btn-outline-primary" @click="moveUp(index)" :disabled="index === 0" title="Move up">
                <i class="fas fa-arrow-up"></i>
              </button>
              <button class="btn btn-outline-primary" @click="moveDown(index)"
                :disabled="index === childNodes.length - 1" title="Move down">
                <i class="fas fa-arrow-down"></i>
              </button>
              <button class="btn btn-outline-danger" @click="removeChild(index)" title="Delete">
                <i class="fas fa-trash"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add a new item -->
    <div>
      <button
        class="btn btn-sm btn-success me-2 mb-2"
        @click="addChildNode"
        :disabled="!isPageNodeSaved"
        :title="isPageNodeSaved ? 'Add an interaction to this page' : 'Save the page first before adding interactions'">
        <i class="fas fa-list-ul me-1"></i> Add a Question
      </button>

      <!-- Message d'information si la page n'est pas sauvegardée -->
      <div v-if="!isPageNodeSaved" class="alert alert-warning mb-4">
        <i class="fas fa-info-circle me-2"></i>
        <strong>Please save this page first!</strong><br>
        <small>Click "Save Page" below to enable adding interactions to this page.</small>
      </div>
    </div>

    <!-- Modale CreateComponent pour nested nodes -->
    <div v-if="showCreateModal" class="modal show d-block" tabindex="-1">
      <div class="modal-dialog modal-xl">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Add Interaction to Page</h5>
            <button type="button" class="btn-close" @click="handleInteractionCancelled"></button>
          </div>
          <div class="modal-body">
            <CreateComponent
              context="nestedNode"
              :test-id="testData._id"
              :page-node-id="pageNode.id"
              :page-node-data="pageNode.data"
              selection-mode="confirm"
              :auto-create="true"
              @confirm="handleInteractionConfirmed"
              @cancel="handleInteractionCancelled"
            />
          </div>
        </div>
      </div>
    </div>
    <div v-if="showCreateModal" class="modal-backdrop show"></div>
<hr>
    <!-- Navigation behavior -->
    <div class="mb-3">
      <label class="form-label">Navigation behavior</label>
      <select v-model="pageData.navigationMode" class="form-select">
        <option value="all-required">All items required</option>
        <option value="any-one">At least one item required</option>
        <option value="free">Free navigation (no validation)</option>
      </select>
      <small class="form-text text-muted">
        Defines whether the student must answer all items before continuing
      </small>
    </div>

    <!-- Scoring mode -->
    <div class="mb-3">
      <label class="form-label">Scoring mode</label>
      <select v-model="pageData.mode" class="form-select">
        <option value="score">Score Mode - Answers count toward total score</option>
        <!-- <option value="options">Options Mode - Answers only affect navigation</option> -->
      </select>
    </div>
  </div>
  <div>
    <div class="d-flex justify-content-end mb-3">
      <button class="btn btn-sm btn-secondary" @click="$emit('cancel')">
        Cancel
      </button>
      <button class="btn btn-sm btn-primary ms-2" @click="save">
        <i class="fas fa-save me-1"></i> Save
      </button>
    </div>

  </div>

</template>


<script setup>
import { ref, computed } from 'vue'
import { useVueFlow } from '@vue-flow/core'
import { v4 as uuidv4 } from 'uuid'
import { useTestStore } from '@/stores/testStore'
import CreateComponent from '@/components/interactions/default/tabCreator/CreateComponent.vue'
import api from '@/services/axios'
const testStore = useTestStore()

const props = defineProps({
  pageNode: {
    type: Object,
    required: true
  },
  allNodes: {
    type: Array,
    required: true
  },
  testData: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['save', 'cancel', 'update:nodes'])

// 🧭 Intégration Vue Flow
const { addNodes, findNode } = useVueFlow()

// État de la modale CreateComponent
const showCreateModal = ref(false)
const newlyAddedNodeId = ref(null)

const pageData = ref({
  label: props.pageNode.data?.label || 'New page',
  description: props.pageNode.data?.description || '',
  navigationMode: props.pageNode.data?.navigationMode || 'all-required',
  mode: props.pageNode.data?.mode || 'score'
})

const availableTypes = [
  { value: 'choice', label: 'Choice', icon: 'fas fa-list-check' },
  { value: 'choiceBlock', label: 'Choice Block', icon: 'fas fa-check-square' },
  { value: 'message', label: 'Message', icon: 'fas fa-comment' },
  { value: 'order', label: 'Order', icon: 'fas fa-sort' },
  { value: 'shortAnswer', label: 'Short Answer', icon: 'fas fa-keyboard' },
  { value: 'textGaps', label: 'Text Gaps', icon: 'fas fa-font' },
  { value: 'textPuzzle', label: 'Text Puzzle', icon: 'fas fa-puzzle-piece' },
  { value: 'makingPairs', label: 'Making Pairs', icon: 'fas fa-link' },
  { value: 'match', label: 'Match', icon: 'fas fa-table' },
  { value: 'hotSpot', label: 'Hot Spot', icon: 'fas fa-map-marker-alt' }
]

// 🔍 Computed list des enfants de la page
const childNodes = computed(() =>
  props.allNodes
    .filter(n => n.parentNode === props.pageNode.id)
    .sort((a, b) => (a.data?.order ?? 999) - (b.data?.order ?? 999))
)

// 🔒 Vérifier si le PageNode est déjà sauvegardé (présent dans allNodes de Vue Flow)
const isPageNodeSaved = computed(() => {
  // Un PageNode est considéré comme sauvegardé s'il existe dans allNodes
  // allNodes provient de nodes.value du VueFlow parent
  if (!props.allNodes || !Array.isArray(props.allNodes)) return false

  return props.allNodes.some(n => n.id === props.pageNode.id)
})

function getTypeLabel(type) {
  const found = availableTypes.find(t => t.value === type)
  return found ? found.label : type
}

// 🧩 Ouverture de la modale CreateComponent
function addChildNode() {
  showCreateModal.value = true
}

// 📥 Gestion de la confirmation depuis CreateComponent
async function handleInteractionConfirmed(payload) {
  console.log('✅ [PageEditor] handleInteractionConfirmed called:', payload)
  console.log('✅ [PageEditor] payload.persisted:', payload.persisted)
  console.log('✅ [PageEditor] payload.node:', payload.node)
  console.log('✅ [PageEditor] pageNode.id:', props.pageNode.id)

  if (payload.persisted && payload.node) {
    console.log('✅ [PageEditor] Adding node to graph...')

    // ✅ ajout dans le graphe principal
    addNodes([payload.node])
    console.log('✅ [PageEditor] Node added via addNodes')

    // ✅ rattachement visuel et logique
    const parent = findNode(props.pageNode.id)
    console.log('✅ [PageEditor] Parent found:', parent)

    if (parent) {
      parent.data.children = parent.data.children || []
      parent.data.children.push(payload.node.id)
      console.log('✅ [PageEditor] Updated parent.data.children:', parent.data.children)
    }

    // ⚠️ NE PAS émettre update:nodes car addNodes() a déjà mis à jour nodes.value
    // emit('update:nodes', [...props.allNodes, payload.node]) <- SUPPRIMÉ car cause la perte du PageNode
    console.log('✅ [PageEditor] Node added successfully, nodes updated by addNodes()')

    // 🔄 Recharger les éléments du test pour synchroniser le testStore
    try {
      console.log('🔄 [PageEditor] Reloading test elements...')
      console.log('🔄 [PageEditor] Looking for element with ID:', payload.node.data?.elementId)

      const { data } = await api.get(`/tests/${props.testData._id}`)

      console.log('🔄 [PageEditor] API response received:', data)
      console.log('🔄 [PageEditor] data.test:', data?.test)
      console.log('🔄 [PageEditor] Elements in response:', data?.test?.elements?.length)

      // L'API retourne { test: {...} }, pas directement les elements
      const elements = data?.test?.elements || []

      if (elements.length > 0) {
        console.log('🔄 [PageEditor] Elements details:', elements.map(e => ({ el_ID: e.el_ID, el_Text: e.el_Text })))

        const targetElement = elements.find(e => e.el_ID === payload.node.data?.elementId)
        if (targetElement) {
          console.log('✅ [PageEditor] Found newly created element:', targetElement)
        } else {
          console.error('❌ [PageEditor] Newly created element NOT FOUND in response!', payload.node.data?.elementId)
        }

        testStore.testData.elements = elements
        console.log('✅ [PageEditor] Test elements reloaded:', elements.length)
        console.log('✅ [PageEditor] testStore.testData.elements updated:', testStore.testData.elements.map(e => ({ el_ID: e.el_ID, el_Text: e.el_Text })))
      } else {
        console.error('❌ [PageEditor] No elements in API response')
      }
    } catch (error) {
      console.error('❌ [PageEditor] Failed to reload test elements:', error)
    }

    // ✨ Déclencher l'animation pulse
    newlyAddedNodeId.value = payload.node.id
    setTimeout(() => {
      newlyAddedNodeId.value = null
    }, 1800) // 3 pulses × 600ms = 1800ms
  } else {
    console.warn('⚠️ [PageEditor] payload.persisted or payload.node is missing')
  }

  showCreateModal.value = false
}

function handleInteractionCancelled() {
  showCreateModal.value = false
}

// 🧱 Ordre et suppression
function moveUp(index) {
  // TODO: Implémenter le déplacement vers le haut
}

function moveDown(index) {
  // TODO: Implémenter le déplacement vers le bas
}

function removeChild(index) {
  // TODO: Implémenter la suppression
}

function save() {
  const updatedNodes = props.allNodes.map(n =>
    n.id === props.pageNode.id
      ? { ...n, data: { ...n.data, ...pageData.value } }
      : n
  )
  emit('save', { pageNode: props.pageNode, pageData: pageData.value, nodes: updatedNodes })
}
</script>


<style scoped>
.page-editor {
  padding: 1rem;
}

.items-list {
  max-height: 400px;
  overflow-y: auto;
}

.item-card {
  padding: 0.75rem;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  background: #f8f9fa;
}

.item-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #0d6efd;
  color: white;
  font-weight: bold;
  font-size: 0.875rem;
}

/* Animation pulse pour l'item nouvellement ajouté */
.pulse-animation {
  animation: pulse 0.6s ease-in-out 3;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(13, 110, 253, 0.7);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 0 10px rgba(13, 110, 253, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(13, 110, 253, 0);
  }
}
</style>
